import React, { useState, useEffect, useCallback } from 'react';
import { supabase, configOk } from './supabase';
import { Building, Plus, LogOut, Trash2, X, Users, Shield, Eye, EyeOff } from 'lucide-react';
import Migraciones from './Migraciones';
import Facturacion, { ClienteVista, borrarFichaCliente } from './Facturacion';
import Alta from './Alta';
import GastoIA from './GastoIA';

// ---------------------------------------------------------------------------
// Corredata Admin — control plane. App SEPARADA (deploy propio) que administra
// las instancias de los clientes desde la base central "corredata-control".
// Cada cliente sigue con su base/deploy aislado; aquí solo se LISTAN y gestionan
// (plan, estado, límites). Auth: super-admins de Corredata (tú, Nadia, Rodrick).
// NOTA íconos: la versión de lucide del repo es vieja → usar solo íconos ya
// probados (Building, Plus, LogOut, Trash2, X, Users, Shield); uno inexistente
// tumba el build. Para estados usamos un punto de color, no un ícono.
// ---------------------------------------------------------------------------

const ESTADOS = {
  prueba:     { label: 'Prueba',     bg: 'var(--info-bg)',    fg: 'var(--info)' },
  activo:     { label: 'Activo',     bg: 'var(--success-bg)', fg: 'var(--success)' },
  suspendido: { label: 'Suspendido', bg: 'var(--warning-bg)', fg: 'var(--warning)' },
  baja:       { label: 'Baja',       bg: 'var(--danger-bg)',  fg: 'var(--danger)' },
};

function Badge({ estado }) {
  const e = ESTADOS[estado] || ESTADOS.prueba;
  return (
    <span className="badge" style={{ background: e.bg, color: e.fg }}>
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: e.fg, marginRight: 6 }} /> {e.label}
    </span>
  );
}

// Registra un evento en la bitácora del control plane (altas, cambios, suspensiones…).
async function logEvento(cliente_id, tipo, detalle, actor) {
  try { await supabase.from('eventos').insert({ cliente_id, tipo, detalle, actor }); } catch { /* no bloquear */ }
}

const TIPO_EVENTO = {
  alta: 'Alta', cambio_datos: 'Cambio de datos', suspension: 'Suspensión',
  reactivacion: 'Reactivación', baja: 'Baja', plan_aplicado: 'Plan aplicado',
  // Lo que el master del cliente hizo por su cuenta desde "Mi Plan" (genera cargo).
  plan_cambiado: 'Cambió su plan', usuario_extra: 'Usuario extra',
  facturado: 'Facturado', facturado_revertido: 'Devuelto a pendiente',
  estado_cuenta_emitido: 'Estado de cuenta', estado_cuenta_anulado: 'Estado de cuenta anulado',
  estado_cuenta_pagado: 'Pago recibido', estado_cuenta_pago_quitado: 'Pago quitado',
  // Acceso de soporte (tanda 4 de seguridad): quién consultó o aplicó la clave de un cliente.
  clave_soporte_vista: 'Clave de soporte consultada', clave_soporte_aplicada: 'Clave de soporte aplicada',
};

function Logo({ size = 28 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48">
      <path d="M35.5 14.4 A15 15 0 1 0 35.5 33.6" stroke="var(--accent)" strokeWidth="5.5" strokeLinecap="round" fill="none" />
      <circle cx="36" cy="24" r="5.5" fill="var(--faint)" />
    </svg>
  );
}

// ── Login ──────────────────────────────────────────────────────────────────
function Login() {
  const [email, setEmail] = useState('');
  const [pwd, setPwd] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const entrar = async (e) => {
    e.preventDefault();
    setErr(''); setBusy(true);
    const { error } = await supabase.auth.signInWithPassword({ email: email.trim(), password: pwd });
    if (error) setErr('Credenciales inválidas.');
    setBusy(false);
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <form onSubmit={entrar} className="card" style={{ width: 360, padding: 28 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'center', marginBottom: 4 }}>
          <Logo size={30} />
          <div style={{ fontSize: 20, fontWeight: 900, letterSpacing: .5 }}>CORRE<span style={{ color: 'var(--faint)' }}>DATA</span></div>
        </div>
        <div style={{ textAlign: 'center', fontSize: 12, color: 'var(--faint)', marginBottom: 22 }}>Admin · Control Plane</div>
        <label className="lbl">Correo</label>
        <input className="input" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="tu@corredata.com" style={{ marginBottom: 14 }} />
        <label className="lbl">Contraseña</label>
        <div style={{ position: 'relative', marginBottom: 18 }}>
          <input className="input" type={showPwd ? 'text' : 'password'} value={pwd} onChange={e => setPwd(e.target.value)} placeholder="••••••••" style={{ paddingRight: 40 }} />
          <button type="button" onClick={() => setShowPwd(s => !s)} aria-label={showPwd ? 'Ocultar contraseña' : 'Ver contraseña'}
            style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--faint)', display: 'flex', padding: 4 }}>
            {showPwd ? <EyeOff size={17} /> : <Eye size={17} />}
          </button>
        </div>
        {err && <div style={{ color: 'var(--danger)', fontSize: 13, marginBottom: 12 }}>{err}</div>}
        <button className="btn btn-primary" type="submit" disabled={busy} style={{ width: '100%', justifyContent: 'center' }}>
          <Shield size={16} /> {busy ? 'Entrando…' : 'Iniciar sesión'}
        </button>
      </form>
    </div>
  );
}

// ── Modal de alta/edición de cliente ────────────────────────────────────────
const VACIO = { nombre: '', slug: '', plan_id: '', estado: 'prueba', limite_usuarios: '', con_ia: false, limite_ia_mes: '', ia_token: '', anon_key: '', app_url: '', supabase_ref: '', contacto_nombre: '', contacto_email: '', notas: '', prueba_hasta: '' };

function ClienteModal({ cliente, planes, actor, onClose, onSaved }) {
  // Los campos vacíos llegan de la base como NULL. Si se copian tal cual, el `.trim()` del
  // guardado revienta con null DESPUÉS de poner "Guardando…" y el modal se queda colgado
  // para siempre sin enviar nada (pasaba con todo cliente que tuviera un texto vacío:
  // Staging sí, Riesgo Unido no). Aquí todo campo del formulario nace con su vacío.
  const [f, setF] = useState(() => {
    if (!cliente) return { ...VACIO };
    const limpio = { ...VACIO };
    for (const k of Object.keys(VACIO)) limpio[k] = cliente[k] ?? VACIO[k];
    return { ...limpio, con_ia: !!cliente.con_ia };
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));

  const guardar = async () => {
    const txt = v => String(v ?? '').trim();
    if (!txt(f.nombre)) { setErr('El nombre es obligatorio.'); return; }
    setBusy(true); setErr('');
    // Cualquier fallo inesperado debe verse como un error, nunca como un "Guardando…" eterno.
    try { await guardarFicha(txt); }
    catch (e) { setBusy(false); setErr('No se pudo guardar: ' + (e?.message || String(e))); }
  };

  const guardarFicha = async (txt) => {
    const payload = {
      nombre: txt(f.nombre),
      slug: txt(f.slug) || null,
      plan_id: f.plan_id || null,
      estado: f.estado,
      limite_usuarios: f.limite_usuarios === '' ? null : Number(f.limite_usuarios),
      con_ia: !!f.con_ia,
      limite_ia_mes: f.limite_ia_mes === '' ? null : Number(f.limite_ia_mes),
      ia_token: txt(f.ia_token) || null,
      anon_key: txt(f.anon_key) || null,
      app_url: txt(f.app_url) || null,
      supabase_ref: txt(f.supabase_ref) || null,
      contacto_nombre: txt(f.contacto_nombre) || null,
      contacto_email: txt(f.contacto_email) || null,
      notas: txt(f.notas) || null,
      prueba_hasta: f.estado === 'prueba' && f.prueba_hasta ? String(f.prueba_hasta).slice(0, 10) : null,
    };
    let error, id = cliente?.id;
    if (cliente?.id) {
      ({ error } = await supabase.from('clientes').update(payload).eq('id', cliente.id));
    } else {
      const r = await supabase.from('clientes').insert(payload).select('id').single();
      error = r.error; id = r.data?.id;
    }
    if (error) { setBusy(false); setErr('No se pudo guardar: ' + error.message); return; }
    await logEvento(id, cliente?.id ? 'cambio_datos' : 'alta', payload.nombre, actor);

    // El plan no sirve de nada si se queda en esta ficha: lo que el cliente VE sale de
    // `cont_config` en su propia base. Al guardar se le aplica ahí mismo, para que
    // portal y app no puedan decir cosas distintas.
    if (payload.plan_id && payload.supabase_ref) {
      // Con tope de tiempo: escribir en la base de otro proyecto puede tardar (o no
      // responder nunca, p. ej. si ese proyecto está pausado). Sin esto el modal se
      // quedaba en "Guardando…" para siempre y no había forma de saber qué pasó.
      const ctrl = new AbortController();
      const corte = setTimeout(() => ctrl.abort(), 45000);
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const r = await fetch('/api/plan', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token}` },
          body: JSON.stringify({ accion: 'aplicar', cliente_id: id }),
          signal: ctrl.signal,
        });
        const res = await r.json();
        if (!res.ok) { setBusy(false); setErr('La ficha SÍ se guardó, pero el plan no se aplicó a la instancia: ' + (res.error || 'error desconocido')); return; }
      } catch (e) {
        setBusy(false);
        setErr(e.name === 'AbortError'
          ? 'La ficha SÍ se guardó, pero la instancia no respondió en 45s. Suele ser que su proyecto de Supabase está pausado o que el ref es incorrecto. Reintenta con Editar → Guardar.'
          : 'La ficha SÍ se guardó, pero el plan no se aplicó a la instancia: ' + (e.message || ''));
        return;
      } finally { clearTimeout(corte); }
    }
    setBusy(false);
    onSaved();
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 22px', borderBottom: '1px solid var(--line)' }}>
          <div style={{ fontSize: 17, fontWeight: 700 }}>{cliente ? 'Editar cliente' : 'Nuevo cliente'}</div>
          <button className="btn btn-sm" onClick={onClose}><X size={16} /></button>
        </div>
        <div style={{ padding: 22, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <div style={{ gridColumn: '1 / -1' }}>
            <label className="lbl">Nombre del corretaje *</label>
            <input className="input" value={f.nombre} onChange={e => set('nombre', e.target.value)} placeholder="Corretaje Ejemplo, S.A." />
          </div>
          <div>
            <label className="lbl">Identificador / slug</label>
            <input className="input" value={f.slug} onChange={e => set('slug', e.target.value)} placeholder="ejemplo" />
          </div>
          <div>
            <label className="lbl">Plan</label>
            <select className="input" value={f.plan_id} onChange={e => set('plan_id', e.target.value)}>
              <option value="">— Sin plan —</option>
              {/* Los planes viejos (Inicial / Pro + IA) quedaron inactivos: no se ofrecen,
                  pero si un cliente todavía apunta a uno se sigue viendo para no cambiárselo
                  sin querer al guardar. */}
              {planes.filter(p => p.activo !== false || String(p.id) === String(f.plan_id))
                     .map(p => <option key={p.id} value={p.id}>{p.nombre}{p.activo === false ? ' (retirado)' : ''}</option>)}
            </select>
          </div>
          <div>
            <label className="lbl">Estado</label>
            <select className="input" value={f.estado} onChange={e => set('estado', e.target.value)}>
              {Object.entries(ESTADOS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>
          {f.estado === 'prueba' && (
            <div>
              <label className="lbl">Prueba hasta</label>
              <input className="input" type="date" value={f.prueba_hasta || ''} onChange={e => set('prueba_hasta', e.target.value)} />
              <div style={{ fontSize: 11, color: 'var(--faint)', marginTop: 4 }}>Al día siguiente su sistema avisa que la licencia venció; a los 5 días se cierra el acceso. Vacío = sin vencimiento.</div>
            </div>
          )}
          {/* Límite de usuarios: se ve el del plan y "ilimitado" es una casilla explícita.
              Por debajo se sigue guardando 0 = ilimitado (que es como lo lee la app del
              cliente), pero un "0" suelto en pantalla se leía como "cero usuarios". */}
          {(() => {
            const plan = planes.find(p => String(p.id) === String(f.plan_id));
            const delPlan = plan?.limite_usuarios ?? null;
            const ilimitado = String(f.limite_usuarios) === '0';
            return (
              <div>
                <label className="lbl">Límite de usuarios</label>
                <input className="input" type="number" min="1"
                  value={ilimitado ? '' : f.limite_usuarios}
                  disabled={ilimitado}
                  onChange={e => set('limite_usuarios', e.target.value)}
                  placeholder={delPlan ? `${delPlan} (del plan ${plan.nombre})` : 'sin límite'} />
                <label style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: 7, fontSize: 13, cursor: 'pointer' }}>
                  <input type="checkbox" checked={ilimitado} style={{ width: 15, height: 15 }}
                    onChange={e => set('limite_usuarios', e.target.checked ? '0' : '')} />
                  Usuarios ilimitados
                </label>
                <div style={{ fontSize: 11.5, color: 'var(--faint)', marginTop: 4 }}>
                  {ilimitado
                    ? 'Este cliente no tendrá tope, aunque su plan traiga uno.'
                    : (delPlan
                        ? `Vacío = usa el del plan (${delPlan}). Escribe un número solo para darle un tope distinto.`
                        : 'Elige un plan para ver el límite que le corresponde.')}
                </div>
              </div>
            );
          })()}

          {/* ── Qué se le va a aplicar a la instancia al guardar ──
              Se muestra ANTES de guardar porque esto escribe en la base del cliente:
              cambia lo que su gente ve en el menú y cuántos usuarios puede dar de alta. */}
          {(() => {
            const plan = planes.find(p => String(p.id) === String(f.plan_id));
            if (!plan) return null;
            const mods = Array.isArray(plan.modulos) ? plan.modulos : [];
            const gateables = mods.filter(m => m !== 'dashboard' && m !== 'admin');
            const limite = f.limite_usuarios === '' ? (plan.limite_usuarios ?? null) : Number(f.limite_usuarios);
            const ilimitado = limite === null || limite === 0;
            const sinRef = !f.supabase_ref?.trim();
            return (
              <div style={{ gridColumn: '1 / -1', padding: '12px 14px', borderRadius: 8,
                background: 'rgba(59,130,246,.07)', border: '1px solid rgba(59,130,246,.25)' }}>
                <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 6 }}>
                  Al guardar se aplicará en la instancia del cliente
                </div>
                {!mods.length ? (
                  <div style={{ fontSize: 12.5, color: 'var(--warn, #b45309)' }}>
                    El plan <b>{plan.nombre}</b> no tiene módulos definidos todavía — corre
                    <code> schema-planes.sql </code> en el control plane. Mientras tanto, guardar no cambiará sus módulos.
                  </div>
                ) : (
                  <>
                    <div style={{ fontSize: 12.5, marginBottom: 8 }}>
                      Plan <b>{plan.nombre}</b> · <b>{gateables.length}</b> módulos ·{' '}
                      {ilimitado ? <b>usuarios ilimitados</b> : <>límite de <b>{limite}</b> usuarios</>}
                    </div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {gateables.map(m => (
                        <span key={m} style={{ padding: '2px 8px', borderRadius: 20, fontSize: 11.5, fontWeight: 600,
                          background: 'rgba(34,197,94,.12)', border: '1px solid rgba(34,197,94,.3)' }}>{m}</span>
                      ))}
                    </div>
                  </>
                )}
                {sinRef && (
                  <div style={{ fontSize: 12, marginTop: 8, color: 'var(--warn, #b45309)' }}>
                    Falta el <b>ref del proyecto Supabase</b> de este cliente: sin eso solo se guarda la
                    ficha y su app seguirá como está.
                  </div>
                )}
              </div>
            );
          })()}
          {/* ── IA: se activa y se limita desde aquí, sin tocar SQL ── */}
          <div style={{ gridColumn: '1 / -1', padding: '12px 14px', borderRadius: 8, background: 'rgba(148,163,184,.08)', border: '1px solid rgba(148,163,184,.2)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <input id="con_ia" type="checkbox" checked={f.con_ia} onChange={e => set('con_ia', e.target.checked)} style={{ width: 16, height: 16 }} />
              <label htmlFor="con_ia" style={{ fontSize: 14, fontWeight: 700 }}>Incluye IA</label>
            </div>
            {f.con_ia && (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 10 }}>
                  <div>
                    <label className="lbl">Análisis con IA / mes (vacío = ilimitado)</label>
                    <input className="input" style={{ width: '100%' }} type="number" placeholder="Base 25 · Pro 150 · Broker 400"
                      value={f.limite_ia_mes ?? ''} onChange={e => set('limite_ia_mes', e.target.value)} />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                    <button type="button" className="btn btn-sm" onClick={() => {
                      const b = new Uint8Array(24); crypto.getRandomValues(b);
                      set('ia_token', Array.from(b, x => x.toString(16).padStart(2, '0')).join(''));
                    }}>{f.ia_token ? 'Regenerar token' : 'Generar token'}</button>
                  </div>
                </div>
                {f.ia_token && (
                  <div>
                    <label className="lbl">Token de la instancia — cópialo al Cloudflare del cliente</label>
                    <input className="input" style={{ width: '100%', fontFamily: 'monospace', fontSize: 12 }}
                      readOnly value={f.ia_token} onFocus={e => e.target.select()} />
                    <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 6, lineHeight: 1.6 }}>
                      En el Cloudflare del cliente: <code>CORREDATA_IA_URL</code> = <code>https://corredata-admin.pages.dev</code> y
                      <code> CORREDATA_IA_TOKEN</code> = este token (como <b>Secreto</b>). Luego un deploy nuevo.
                      <br />Si <b>regeneras</b> el token, el anterior deja de servir y la IA de ese cliente falla hasta que actualice la variable.
                    </div>
                  </div>
                )}
                <div style={{ marginTop: 10 }}>
                  <label className="lbl">Llave pública (anon) de la Supabase del cliente</label>
                  <input className="input" style={{ width: '100%', fontFamily: 'monospace', fontSize: 12 }}
                    placeholder="sb_publishable_… o eyJ…"
                    value={f.anon_key || ''} onChange={e => set('anon_key', e.target.value)} />
                  <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 6, lineHeight: 1.6 }}>
                    Con esto el proxy valida la sesión del usuario contra la Supabase del cliente, y la IA
                    funciona <b>sin poner nada</b> en su Cloudflare: se prende y se apaga desde aquí.
                    Es la llave <b>publicable</b> (la que ya vive en el navegador), no la secreta.
                  </div>
                </div>
                <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 8 }}>
                  El <b>Copiloto</b> (guía de uso) es ilimitado y no descuenta. Solo cuentan los análisis de documentos.
                  Si el cliente agota el cupo, amplía solo desde su sistema (+50 por B/.7) y el cargo queda pendiente de facturar.
                </div>
              </>
            )}
          </div>
          <div>
            <label className="lbl">URL de la app</label>
            <input className="input" value={f.app_url} onChange={e => set('app_url', e.target.value)} placeholder="https://cliente.pages.dev" />
          </div>
          <div>
            <label className="lbl">Ref. Supabase del cliente</label>
            <input className="input" value={f.supabase_ref} onChange={e => set('supabase_ref', e.target.value)} placeholder="abcd…" />
          </div>
          <div>
            <label className="lbl">Contacto</label>
            <input className="input" value={f.contacto_nombre} onChange={e => set('contacto_nombre', e.target.value)} placeholder="Nombre" />
          </div>
          <div>
            <label className="lbl">Correo de contacto</label>
            <input className="input" value={f.contacto_email} onChange={e => set('contacto_email', e.target.value)} placeholder="correo@cliente.com" />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label className="lbl">Notas</label>
            <textarea className="input" value={f.notas} onChange={e => set('notas', e.target.value)} rows={2} style={{ height: 'auto', padding: '10px 12px' }} />
          </div>
          {err && <div style={{ gridColumn: '1 / -1', color: 'var(--danger)', fontSize: 13 }}>{err}</div>}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '16px 22px', borderTop: '1px solid var(--line)' }}>
          <button className="btn" onClick={onClose}>Cancelar</button>
          <button className="btn btn-primary" onClick={guardar} disabled={busy}>{busy ? 'Guardando…' : 'Guardar'}</button>
        </div>
      </div>
    </div>
  );
}

// ── Panel principal ─────────────────────────────────────────────────────────
function Panel({ user }) {
  const [clientes, setClientes] = useState([]);
  const [planes, setPlanes] = useState([]);
  const [eventos, setEventos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | {} (nuevo) | cliente (editar)
  const [alta, setAlta] = useState(false);   // asistente de alta automática (provisioning)
  const [vista, setVista] = useState('clientes'); // 'clientes' | 'migraciones' | 'cargos'
  const [ver, setVer] = useState(null);           // cliente cuya ficha está abierta ("Ver")
  const actor = user?.email;

  const load = useCallback(async () => {
    setLoading(true);
    const [{ data: cs }, { data: ps }, { data: ev }] = await Promise.all([
      supabase.from('clientes').select('*, planes(nombre, limite_usuarios)').order('fecha_alta', { ascending: false }),
      supabase.from('planes').select('*').order('id'),
      supabase.from('eventos').select('*, clientes(nombre)').order('creado_en', { ascending: false }).limit(12),
    ]);
    setClientes(cs || []);
    setPlanes(ps || []);
    setEventos(ev || []);
    setLoading(false);
    return cs || [];
  }, []);
  useEffect(() => { load(); }, [load]);

  const cambiarEstado = async (c, nuevo) => {
    await supabase.from('clientes').update({ estado: nuevo }).eq('id', c.id);
    await logEvento(c.id, nuevo === 'suspendido' ? 'suspension' : 'reactivacion', c.nombre, actor);
    // Suspender cierra el acceso a SU sistema en el acto; reactivar lo devuelve. Antes la
    // suspensión solo cambiaba esta ficha y el cliente seguía entrando como si nada.
    if (c.supabase_ref) {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const r = await fetch('/api/plan', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token}` },
          body: JSON.stringify({ accion: 'licencia', cliente_id: c.id }),
        }).then(x => x.json());
        if (!r.ok) window.alert(`El estado se guardó aquí, pero no se pudo aplicar en su sistema: ${r.error || 'error desconocido'}`);
      } catch (e) { window.alert(`El estado se guardó aquí, pero no se pudo aplicar en su sistema: ${e.message || ''}`); }
    }
    load();
  };

  const eliminar = async (c) => {
    if (!window.confirm(`¿Eliminar el registro de "${c.nombre}"? (No borra su instancia, solo el registro aquí.)`)) return;
    // Antes el error se tragaba: la bitácora lo referencia y el borrado fallaba sin avisar.
    const b = await borrarFichaCliente(c.id);
    if (b.error) { window.alert(`No se pudo eliminar "${c.nombre}": ${b.error}`); return; }
    await logEvento(null, 'baja', c.nombre, actor);
    load();
  };

  const total = clientes.length;
  const activos = clientes.filter(c => c.estado === 'activo').length;
  const prueba = clientes.filter(c => c.estado === 'prueba').length;
  // 0 guardado = ilimitado. En la lista NUNCA se muestra como "0", que se leería
  // como "no puede dar de alta a nadie".
  const limiteEfectivo = (c) => {
    const v = c.limite_usuarios ?? c.planes?.limite_usuarios ?? null;
    if (v === null || Number(v) === 0) return '∞';
    // Los usuarios extra que el cliente contrató desde "Mi Plan" se suman al tope.
    const extra = Number(c.usuarios_extra) || 0;
    return extra ? `${v} + ${extra}` : v;
  };

  if (vista === 'migraciones') return <Migraciones actor={actor} onSalir={() => { setVista('clientes'); load(); }} />;
  if (vista === 'cargos') return <Facturacion actor={actor} onSalir={() => { setVista('clientes'); load(); }} />;

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '22px 20px 60px' }}>
      {/* Header */}
      <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 26 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Logo size={30} />
          <div>
            <div style={{ fontSize: 18, fontWeight: 900, letterSpacing: .5 }}>CORRE<span style={{ color: 'var(--faint)' }}>DATA</span> <span style={{ fontWeight: 600, color: 'var(--muted)', fontSize: 14 }}>Admin</span></div>
            <div style={{ fontSize: 11, color: 'var(--faint)' }}>Control plane · administra clientes</div>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 13, color: 'var(--muted)' }}>{user?.email}</span>
          <button className="btn btn-sm" onClick={() => setVista('cargos')} title="Emitir estados de cuenta, registrar pagos y ver lo que cada cliente aceptó y falta cobrarle">Facturación</button>
          <button className="btn btn-sm" onClick={() => setVista('migraciones')} title="Aplicar cambios de esquema a las bases de los clientes">Migraciones</button>
          <button className="btn btn-sm" onClick={() => supabase.auth.signOut()}><LogOut size={15} /> Salir</button>
        </div>
      </header>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 22 }}>
        <Stat icon={Building} label="Clientes" value={total} />
        <Stat label="Activos" value={activos} color="var(--success)" />
        <Stat label="En prueba" value={prueba} color="var(--info)" />
      </div>

      {/* Gasto de IA: aviso si el mes se acerca al presupuesto o un cliente se dispara */}
      {!loading && <GastoIA clientes={clientes} />}

      {/* Toolbar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ fontSize: 16, fontWeight: 700 }}>Clientes</div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn btn-sm" onClick={() => setAlta(true)} title="Crea la base del cliente (proyecto Supabase, esquema, catálogos, master) y lo registra con su plan">Alta automática</button>
          <button className="btn btn-primary" onClick={() => setModal({})}><Plus size={16} /> Nuevo cliente</button>
        </div>
      </div>

      {/* Tabla */}
      <div className="card" style={{ overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--faint)' }}>Cargando…</div>
        ) : clientes.length === 0 ? (
          <div style={{ padding: 48, textAlign: 'center', color: 'var(--faint)' }}>
            <Building size={30} style={{ opacity: .5 }} />
            <div style={{ marginTop: 10, fontSize: 15, fontWeight: 600, color: 'var(--muted)' }}>Aún no hay clientes</div>
            <div style={{ fontSize: 13 }}>Da de alta el primero con “Nuevo cliente”.</div>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table>
              <thead>
                <tr>
                  <th>Corretaje</th><th>Plan</th><th>Estado</th><th>Usuarios</th><th>IA</th><th>Alta</th><th></th>
                </tr>
              </thead>
              <tbody>
                {clientes.map(c => (
                  <tr key={c.id}>
                    <td>
                      <div style={{ fontWeight: 600 }}>{c.nombre}</div>
                      {c.app_url && <a href={c.app_url} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: 'var(--info)' }}>{c.app_url.replace(/^https?:\/\//, '')}</a>}
                    </td>
                    <td>{c.planes?.nombre || <span style={{ color: 'var(--faint)' }}>—</span>}</td>
                    <td><Badge estado={c.estado} /></td>
                    <td><span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, color: 'var(--muted)' }}><Users size={14} /> {limiteEfectivo(c)}</span></td>
                    <td>{c.con_ia ? <span className="badge" style={{ background: 'var(--surface-2)', color: 'var(--accent-soft)', border: '1px solid var(--line)' }}>IA</span> : <span style={{ color: 'var(--faint)' }}>—</span>}</td>
                    <td style={{ color: 'var(--muted)', fontSize: 13 }}>{c.fecha_alta ? new Date(c.fecha_alta).toLocaleDateString('es-PA') : '—'}</td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {c.estado === 'suspendido' ? (
                        <button className="btn btn-sm" onClick={() => cambiarEstado(c, 'activo')} title="Reactivar" style={{ color: 'var(--success)' }}>Reactivar</button>
                      ) : (
                        <button className="btn btn-sm" onClick={() => cambiarEstado(c, 'suspendido')} title="Suspender" style={{ color: 'var(--warning)' }}>Suspender</button>
                      )}
                      <button className="btn btn-sm" onClick={() => setVer(c)} title="Ver todo lo que tiene este cliente (desde ahí se edita)" style={{ marginLeft: 6 }}>Ver</button>
                      <button className="btn btn-sm btn-danger" onClick={() => eliminar(c)} title="Eliminar" style={{ marginLeft: 6 }}><Trash2 size={14} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Actividad reciente */}
      {eventos.length > 0 && (
        <div style={{ marginTop: 26 }}>
          <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 12 }}>Actividad reciente</div>
          <div className="card" style={{ padding: '6px 0' }}>
            {eventos.map(e => (
              <div key={e.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '9px 18px', borderBottom: '1px solid var(--line)' }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--accent-soft)', minWidth: 130 }}>{TIPO_EVENTO[e.tipo] || e.tipo}</span>
                <span style={{ fontSize: 14, flex: 1 }}>{e.clientes?.nombre || e.detalle || '—'}</span>
                <span style={{ fontSize: 12, color: 'var(--faint)' }}>{e.actor}</span>
                <span style={{ fontSize: 12, color: 'var(--faint)', minWidth: 120, textAlign: 'right' }}>{e.creado_en ? new Date(e.creado_en).toLocaleString('es-PA', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* "Ver": la ficha completa del cliente. Editar se abre DESDE aquí, encima de la ficha. */}
      {ver && (
        <ClienteVista
          key={`${ver.id}-${ver.plan_id}-${ver.estado}-${ver.usuarios_extra}`}
          cliente={ver}
          planes={planes}
          actor={actor}
          badge={<Badge estado={ver.estado} />}
          onEditar={() => setModal(ver)}
          onClose={() => { setVer(null); load(); }}
        />
      )}

      {alta && (
        <Alta
          planes={planes}
          actor={actor}
          logEvento={logEvento}
          onClose={() => { setAlta(false); load(); }}
          onHecho={() => { setAlta(false); load(); }}
        />
      )}

      {modal !== null && (
        <ClienteModal
          cliente={modal.id ? modal : null}
          planes={planes}
          actor={actor}
          onClose={() => setModal(null)}
          onSaved={async () => {
            setModal(null);
            const cs = await load();
            // Si se editó desde la ficha, la ficha muestra lo recién guardado.
            setVer(v => (v ? (cs.find(x => x.id === v.id) || null) : null));
          }}
        />
      )}
    </div>
  );
}

function Stat({ icon: Icon, label, value, color }) {
  return (
    <div className="card" style={{ padding: '16px 18px', display: 'flex', alignItems: 'center', gap: 14 }}>
      <div style={{ width: 40, height: 40, borderRadius: 10, background: 'var(--surface-2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        {Icon ? <Icon size={20} style={{ color: color || 'var(--accent-soft)' }} /> : <span style={{ fontSize: 18, fontWeight: 900, color: color || 'var(--accent-soft)' }}>•</span>}
      </div>
      <div>
        <div style={{ fontSize: 24, fontWeight: 900, lineHeight: 1, color: color || 'var(--ink)' }}>{value}</div>
        <div style={{ fontSize: 12, color: 'var(--faint)', textTransform: 'uppercase', letterSpacing: .4, marginTop: 3 }}>{label}</div>
      </div>
    </div>
  );
}

// ── App ─────────────────────────────────────────────────────────────────────
export default function App() {
  const [session, setSession] = useState(undefined); // undefined = cargando

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setSession(data.session));
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => setSession(s));
    return () => sub?.subscription?.unsubscribe();
  }, []);

  if (!configOk) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 30, textAlign: 'center' }}>
        <div className="card" style={{ padding: 28, maxWidth: 460 }}>
          <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 8 }}>Falta configuración</div>
          <div style={{ fontSize: 14, color: 'var(--muted)' }}>Define <code>REACT_APP_SUPABASE_URL</code> y <code>REACT_APP_SUPABASE_KEY</code> (de corredata-control) en las variables del deploy.</div>
        </div>
      </div>
    );
  }
  if (session === undefined) return <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--faint)' }}>Cargando…</div>;
  return session ? <Panel user={session.user} /> : <Login />;
}
